import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, GitBranch, Link, Circle, Loader2 } from 'lucide-react';

interface NodeContextMenuProps {
  /** Menu position and target node */
  contextMenu: { x: number; y: number; nodeId: string } | null;
  /** Is delete operation in progress */
  isDeleting: boolean;
  /** Connect to node handler */
  onConnect: (nodeId: string) => void;
  /** Branch from node handler */
  onBranch: (nodeId: string) => void;
  /** Change status handler */
  onChangeStatus: (nodeId: string) => void;
  /** Delete node handler */
  onDelete: (nodeId: string) => void;
}

/**
 * Context menu component for node operations.
 * Displays options for connect, branch, change status, and delete.
 */
export function NodeContextMenu({
  contextMenu,
  isDeleting,
  onConnect,
  onBranch,
  onChangeStatus,
  onDelete,
}: NodeContextMenuProps) {
  if (!contextMenu) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="fixed z-50 bg-surface-800 border border-surface-600 rounded-lg shadow-xl py-1 min-w-[140px]"
      style={{ left: contextMenu.x, top: contextMenu.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => onConnect(contextMenu.nodeId)}
        className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-surface-700 flex items-center gap-2"
      >
        <Link className="w-4 h-4" />
        Connect to...
      </button>
      <button
        onClick={() => onBranch(contextMenu.nodeId)}
        className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-surface-700 flex items-center gap-2"
      >
        <GitBranch className="w-4 h-4" />
        Branch
      </button>
      <button
        onClick={() => onChangeStatus(contextMenu.nodeId)}
        className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-surface-700 flex items-center gap-2"
      >
        <Circle className="w-4 h-4" />
        Change Status...
      </button>
      <div className="h-px bg-surface-600 my-1" />
      <button
        onClick={() => onDelete(contextMenu.nodeId)}
        disabled={isDeleting}
        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 disabled:opacity-50"
      >
        {isDeleting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Trash2 className="w-4 h-4" />
        )}
        Delete Task
      </button>
    </motion.div>
  );
}
